import React, { useEffect, useState } from 'react'
import { Flex, Grid, Spinner, Box, IconButton, Tooltip } from '@chakra-ui/react'
import Card from './Card'
import NotFound from './NotFound'
import { useGetGroupsDataQuery } from '../api/automationsApi'
import { AddIcon } from '@chakra-ui/icons'
import { useInView } from 'react-intersection-observer'
import { useDeviceStatusContext } from '../../../context/DeviceStatusContext'
import { findDeviceBrightness, findDeviceOutput } from '../../../utils/helpers'

interface GroupsProps {
  querySearchParams: any
  deviceId: number
  groupId: number
  locationId: number
  searchInput: string
  hasAutomation?: boolean
  handleOpenModal: (device: any) => void
}

export default function Groups({
  querySearchParams,
  deviceId,
  groupId,
  locationId,
  searchInput,
  hasAutomation,
  handleOpenModal, }: GroupsProps) {
  const [page, setPage] = useState(1)
  const [allGroups, setAllGroups] = useState<any[]>([])
  const [brightnessMap, setBrightnessMap] = useState<Record<number, number>>({})
  const { deviceStatusData } = useDeviceStatusContext()

  const { data, isLoading, isFetching } = useGetGroupsDataQuery({
    groups: groupId,
    locations: locationId,
    devices: deviceId,
    search: searchInput,
    has_automation: hasAutomation,
    page: searchInput || groupId || locationId || deviceId ? 1 : page,
  })

  const { ref, inView, entry } = useInView({
    threshold: 1.0,
  })
  useEffect(() => {
    setPage(1)
    setAllGroups([])
  }, [searchInput, groupId, locationId, deviceId, hasAutomation])

  useEffect(() => {
    if (inView && entry.isIntersecting && !isFetching && data?.next) {
      setPage((prev) => prev + 1)
    }
  }, [inView, entry, isFetching, data?.next])

  useEffect(() => {
    if (data?.results) {
      setAllGroups((prev) => {
        return data.results.reduce((acc, incomingGroup) => {
          const existingIndex = acc.findIndex((d) => d.id === incomingGroup.id)

          if (existingIndex !== -1) {
            const existingGroup = acc[existingIndex]

            // Only update if active_automations or inactive_automations has changed P.s this need to be validated if works the same way for the infinity scroll
            if (existingGroup.active_automations !== incomingGroup.active_automations || existingGroup.inactive_automations !== incomingGroup.inactive_automations) {
              acc[existingIndex] = incomingGroup
            }
          } else {
            acc.push(incomingGroup)
          }

          return [...acc]
        }, [...prev])
      })
    }
  }, [data])

  if ((isLoading || isFetching) && page === 1) {
    return (
      <Flex justifyContent="center" alignItems="center" height="200px">
        <Spinner size="xl" />
      </Flex>
    )
  }

  if (allGroups.length === 0) {
    return (
      <NotFound
        title="No smart groups"
        description="Currently there are no groups that support automation and smart control"
        header={false}
      />
    )
  }
  const findGroupState = (group): boolean | 'mixed' => {
    const groupsState = group?.devices.map(x => x.model_channel_topics?.map((y) => findDeviceOutput(x.id, y.title, deviceStatusData))).flat() 

    const allSame = groupsState.every((val) => val === groupsState[0]);
    if (allSame) {
      return groupsState[0]
    } else {
      return 'mixed'
    }
  }

  const findGroupBrightness = (group) => {
    const groupsState = group?.devices.map(x => x.model_channel_topics?.map((y) => findDeviceBrightness(x.id, y.title, deviceStatusData))).flat()
    const validValues = groupsState.filter((val): val is number => typeof val === 'number');
    if (validValues.length === 0) {
      return undefined
    } else {
      return Math.max(...validValues);
    }
  }

  return (
    <Grid templateColumns="repeat(4, 1fr)" gap={4}>
      {allGroups.map((group) =>
        <Card
          title={group.name}
          id={group.id}
          key={`${group.id}`}
          toggle={findGroupState(group)}
          activeAutomations={group.active_automations}
          inactiveAutomations={group.inactive_automations}
          brightness={brightnessMap[group.id] ?? findGroupBrightness(group)}
          onBrightnessChange={(value) => {
            setBrightnessMap((prev) => ({
              ...prev,
              [group.id]: value,
            }))
          }}
          actions={
            <Tooltip placement="top" label="New automation" hasArrow offset={[0, -5]}>
              <IconButton
                icon={<AddIcon />}
                aria-label="Settings"
                variant="unstyled"
                color="blue.500"
                size="lg"
                position="absolute"
                top="-1"
                right="0"
                zIndex="1"
                _focus={{ boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenModal(group)
                }}
              />
            </Tooltip>
          }
        />
      )}
      {/* Infinite scroll sentinel */}
      <Box
        ref={ref}
        height="40px"
        gridColumn="1 / -1"
        display="flex"
        justifyContent="center"
      >
        {isFetching && <Spinner />}
      </Box>
    </Grid>
  )
}
